import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  assistantAPI,
  MembershipsResponse,
  UpdateMembershipsPayload,
  UserMembership,
} from '../api/assistantAPI';

export type { UserMembership, MembershipsResponse };

export const assistantMembershipKeys = {
  all: ['assistant-memberships'] as const,
  detail: (assistantId: string) =>
    [...assistantMembershipKeys.all, assistantId] as const,
};

// GET /api/assistants/{assistant_id}/memberships
export const getAssistantMembershipsQueryOptions = (
  assistantId: string,
  isOwner: boolean
) =>
  queryOptions<MembershipsResponse>({
    queryKey: assistantMembershipKeys.detail(assistantId),
    queryFn: async () => {
      return assistantAPI.getAssistantMembershipsAsync(assistantId);
    },
    enabled: !!assistantId && isOwner,
    staleTime: Infinity,
  });

/** Compatibility wrapper for incremental migration to query option factories. */
export const useGetAssistantMemberships = (
  assistantId: string,
  isOwner: boolean
) => useQuery(getAssistantMembershipsQueryOptions(assistantId, isOwner));

// POST /api/assistants/{assistant_id}/memberships
export const membershipMutationKey = (assistantId: string) =>
  ['assistant-memberships', assistantId] as const;

const applyOptimisticMemberships = (
  old: MembershipsResponse,
  payload: UpdateMembershipsPayload
): MembershipsResponse => {
  const promotedAdminIds = new Set(payload.admin_users_id_list ?? []);
  const regularUserIds = new Set(payload.access_users_id_list ?? []);

  // A POST with access_users_id_list demotes admins to regular members.
  const demotedUsers = old.admins.filter(
    (admin) => admin.type === 'user' && regularUserIds.has(admin.id)
  );
  const existingAccessIds = new Set(old.accessUsers.map((u) => u.userId));

  const newRegularUsers = [...regularUserIds]
    .filter((id) => !promotedAdminIds.has(id) && !existingAccessIds.has(id))
    .map((userId) => {
      const admin = demotedUsers.find((a) => a.id === userId);
      return {
        userId,
        userEmail: admin?.email ?? admin?.name ?? userId,
        ...(admin?.name ? { userName: admin.name } : {}),
      };
    });

  const accessUsers = [
    ...old.accessUsers.filter((u) => !promotedAdminIds.has(u.userId)),
    ...newRegularUsers,
  ];

  const retainedAdmins = old.admins.filter(
    (admin) => admin.type !== 'user' || !regularUserIds.has(admin.id)
  );

  const addedAdmins = [...promotedAdminIds]
    .filter(
      (id) => !retainedAdmins.some((a) => a.id === id && a.type === 'user')
    )
    .map((userId) => {
      const existing = old.accessUsers.find((u) => u.userId === userId);
      return {
        id: userId,
        name: existing?.userName ?? existing?.userEmail ?? userId,
        email: existing?.userEmail ?? null,
        type: 'user' as const,
      };
    });

  return { ...old, accessUsers, admins: [...retainedAdmins, ...addedAdmins] };
};

type AddMembershipMutationPayload = UpdateMembershipsPayload & {
  /** New shares rely on the membership GET to supply canonical display values. */
  skip_optimistic_update?: boolean;
};

export const useAddAssistantMemberships = (assistantId: string) => {
  const queryClient = useQueryClient();
  const queryKey = assistantMembershipKeys.detail(assistantId);
  const mutationKey = membershipMutationKey(assistantId);

  return useMutation<void, Error, AddMembershipMutationPayload>({
    mutationKey,
    // Serializes server requests only; it does not make onMutate snapshots safe.
    scope: { id: `assistant-memberships:${assistantId}` },
    mutationFn: async (payload) => {
      const { skip_optimistic_update: _skipOptimisticUpdate, ...apiPayload } =
        payload;
      await assistantAPI.addAssistantMembershipsAsync(assistantId, apiPayload);
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey });

      if (payload.skip_optimistic_update) return;

      const hasChanges =
        (payload.admin_users_id_list?.length ?? 0) > 0 ||
        (payload.access_users_id_list?.length ?? 0) > 0;
      if (!hasChanges) return;

      queryClient.setQueryData<MembershipsResponse>(queryKey, (old) =>
        old ? applyOptimisticMemberships(old, payload) : old
      );
    },
    // No snapshot restore: the refetch below is the rollback.
    onSettled: () => {
      // The current mutation still counts as in flight inside onSettled.
      if (queryClient.isMutating({ mutationKey }) <= 1) {
        return queryClient.invalidateQueries({
          queryKey,
          refetchType: 'active',
        });
      }
    },
  });
};

// DELETE /api/assistants/{assistant_id}/memberships
export const useRemoveAssistantMemberships = (assistantId: string) => {
  const queryClient = useQueryClient();
  const queryKey = assistantMembershipKeys.detail(assistantId);

  return useMutation<void, Error, UpdateMembershipsPayload>({
    mutationFn: async (payload) => {
      await assistantAPI.removeAssistantMembershipsAsync(assistantId, payload);
    },

    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey });

      const previousMembers =
        queryClient.getQueryData<MembershipsResponse>(queryKey); // ← MembershipsResponse

      const removedSet = new Set(payload.access_users_id_list ?? []);
      const removedAdminUserIds = new Set(payload.admin_users_id_list ?? []);
      queryClient.setQueryData<MembershipsResponse>(queryKey, (old) =>
        old
          ? {
              ...old,
              accessUsers: old.accessUsers.filter(
                (u) => !removedSet.has(u.userId)
              ),
              admins: old.admins.filter(
                (admin) =>
                  admin.type !== 'user' || !removedAdminUserIds.has(admin.id)
              ),
            }
          : old
      );

      return { previousMembers };
    },

    onError: (_err, _payload, context) => {
      const ctx = context as { previousMembers?: MembershipsResponse }; // ← MembershipsResponse
      if (ctx?.previousMembers !== undefined) {
        queryClient.setQueryData(queryKey, ctx.previousMembers);
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'active',
      });
    },
  });
};
